import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Input } from '@/components/ui/input'
import { useUpdatePlanName } from '@/hooks/useAssortmentPlans'
import { useCurrentVersionStore } from '@/stores/useCurrentVersionStore'
import { VersionPickerModal } from './VersionPickerModal'
import type { AssortmentPlan } from '@/types'

const CLICK_DELAY = 250 // ms to distinguish single from double click

export function EditablePlanNameCell(
  props: CustomCellRendererProps<AssortmentPlan>
) {
  const plan = props.data
  const navigate = useNavigate()
  const updatePlanName = useUpdatePlanName()
  const clearCurrentVersion = useCurrentVersionStore(s => s.clearCurrentVersion)

  const [isEditing, setIsEditing] = useState(false)
  const [showVersionPicker, setShowVersionPicker] = useState(false)
  const [editValue, setEditValue] = useState(plan?.name || '')
  const inputRef = useRef<HTMLInputElement>(null)
  const clickTimerRef = useRef<number | null>(null)

  // Reset editValue when plan name changes externally
  useEffect(() => {
    if (!isEditing && plan?.name) {
      setEditValue(plan.name)
    }
  }, [plan?.name, isEditing])

  // Auto-focus and select input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSingleClick = useCallback(() => {
    if (!plan) return

    // Clear any existing timer first (prevents double-click race condition)
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
    }

    // Set a timer to navigate - will be cancelled if double-click occurs
    clickTimerRef.current = window.setTimeout(() => {
      if (plan.versions_count > 1) {
        setShowVersionPicker(true)
      } else {
        clearCurrentVersion(plan.id)
        navigate(`/plans/${plan.id}`)
      }
    }, CLICK_DELAY)
  }, [navigate, plan, clearCurrentVersion])

  const handleDoubleClick = useCallback(() => {
    // Cancel the pending navigation
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
      clickTimerRef.current = null
    }

    if (plan?.is_frozen) return

    setIsEditing(true)
  }, [plan?.is_frozen])

  const handleSave = useCallback(() => {
    const trimmedValue = editValue.trim()

    // Don't save if empty
    if (!trimmedValue) {
      setEditValue(plan?.name || '')
      setIsEditing(false)
      return
    }

    // Don't call API if value hasn't changed
    if (trimmedValue === plan?.name) {
      setIsEditing(false)
      return
    }

    if (plan?.id) {
      updatePlanName.mutate(
        { planId: plan.id, name: trimmedValue },
        {
          onSettled: () => {
            setIsEditing(false)
          },
        }
      )
    }
  }, [editValue, plan?.id, plan?.name, updatePlanName])

  const handleCancel = useCallback(() => {
    setEditValue(plan?.name || '')
    setIsEditing(false)
  }, [plan?.name])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleBlur = () => {
    handleSave()
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current)
      }
    }
  }, [])

  if (!plan) return null

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        maxLength={255}
        className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0 font-medium text-sm"
      />
    )
  }

  return (
    <>
      <span
        onClick={handleSingleClick}
        onDoubleClick={handleDoubleClick}
        title={plan.is_frozen ? 'Unfreeze the plan to rename it' : undefined}
        className="font-medium text-sm text-foreground hover:text-primary hover:underline cursor-pointer"
      >
        {plan.name}
      </span>

      {showVersionPicker && (
        <VersionPickerModal
          open={showVersionPicker}
          onOpenChange={setShowVersionPicker}
          planId={plan.id}
          planName={plan.name}
        />
      )}
    </>
  )
}
